import React, { useState } from 'react';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Upload, Shield, AlertTriangle, CheckCircle, Loader2 } from 'lucide-react';

const IDVerificationModal = ({ isOpen, onClose, onVerified, withdrawAmount }) => {
  const [step, setStep] = useState(1);
  const [idType, setIdType] = useState('');
  const [frontImage, setFrontImage] = useState(null);
  const [backImage, setBackImage] = useState(null);
  const [selfie, setSelfie] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');

  const idTypes = [
    { value: 'drivers_license', label: "Driver's License" },
    { value: 'passport', label: 'Passport' },
    { value: 'state_id', label: 'State ID Card' },
    { value: 'military_id', label: 'Military ID' }
  ];

  const handleFileChange = (setter) => (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 10 * 1024 * 1024) {
        setError('File size must be less than 10MB');
        return;
      }
      if (!['image/jpeg', 'image/png', 'image/heic'].includes(file.type)) {
        setError('Please upload a JPG, PNG, or HEIC image');
        return;
      }
      setter(file);
      setError('');
    }
  };

  const handleSubmit = async () => {
    if (!idType || !frontImage || !selfie) {
      setError('Please complete all required fields');
      return;
    }
    
    setIsSubmitting(true);
    
    // Simulate verification process
    setTimeout(() => {
      setIsSubmitting(false);
      setStep(3);
      // In production, this would upload to secure storage and trigger manual review
    }, 2000);
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-xl">
            <Shield className="h-6 w-6 text-emerald-500" />
            Identity Verification
          </DialogTitle>
        </DialogHeader>
        
        <div className="space-y-6">
          {step === 1 && (
            <>
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                <div className="flex gap-3">
                  <AlertTriangle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <h4 className="font-medium text-amber-800">Verification Required</h4>
                    <p className="text-amber-700 text-sm mt-1">
                      To withdraw <strong>${withdrawAmount}</strong>, we need to verify your identity. 
                      This is a one-time process required by financial regulations.
                    </p>
                  </div>
                </div>
              </div>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Select ID Type *</label>
                  <select
                    value={idType}
                    onChange={(e) => setIdType(e.target.value)}
                    className="w-full p-3 border rounded-lg bg-white"
                  >
                    <option value="">Choose ID type...</option>
                    {idTypes.map(type => (
                      <option key={type.value} value={type.value}>{type.label}</option>
                    ))}
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-medium mb-2">Front of ID *</label>
                  <div className="border-2 border-dashed rounded-lg p-4 text-center hover:border-emerald-500 transition-colors cursor-pointer">
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleFileChange(setFrontImage)}
                      className="hidden"
                      id="front-upload"
                    />
                    <label htmlFor="front-upload" className="cursor-pointer">
                      {frontImage ? (
                        <div className="flex items-center justify-center gap-2 text-emerald-600">
                          <CheckCircle className="h-5 w-5" />
                          <span>{frontImage.name}</span>
                        </div>
                      ) : (
                        <>
                          <Upload className="h-8 w-8 mx-auto text-gray-400 mb-2" />
                          <p className="text-sm text-gray-600">Click to upload front of ID</p>
                        </>
                      )}
                    </label>
                  </div>
                </div>
                
                {idType && idType !== 'passport' && (
                  <div>
                    <label className="block text-sm font-medium mb-2">Back of ID (if applicable)</label>
                    <div className="border-2 border-dashed rounded-lg p-4 text-center hover:border-emerald-500 transition-colors cursor-pointer">
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleFileChange(setBackImage)}
                        className="hidden"
                        id="back-upload"
                      />
                      <label htmlFor="back-upload" className="cursor-pointer">
                        {backImage ? (
                          <div className="flex items-center justify-center gap-2 text-emerald-600">
                            <CheckCircle className="h-5 w-5" />
                            <span>{backImage.name}</span>
                          </div>
                        ) : (
                          <>
                            <Upload className="h-8 w-8 mx-auto text-gray-400 mb-2" />
                            <p className="text-sm text-gray-600">Click to upload back of ID</p>
                          </>
                        )}
                      </label>
                    </div>
                  </div>
                )}
                
                <div>
                  <label className="block text-sm font-medium mb-2">Selfie with ID *</label>
                  <div className="border-2 border-dashed rounded-lg p-4 text-center hover:border-emerald-500 transition-colors cursor-pointer">
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleFileChange(setSelfie)}
                      className="hidden"
                      id="selfie-upload"
                    />
                    <label htmlFor="selfie-upload" className="cursor-pointer">
                      {selfie ? (
                        <div className="flex items-center justify-center gap-2 text-emerald-600">
                          <CheckCircle className="h-5 w-5" />
                          <span>{selfie.name}</span>
                        </div>
                      ) : (
                        <>
                          <Upload className="h-8 w-8 mx-auto text-gray-400 mb-2" />
                          <p className="text-sm text-gray-600">Take a selfie holding your ID</p>
                          <p className="text-xs text-gray-400 mt-1">Make sure your face and ID are clearly visible</p>
                        </>
                      )}
                    </label>
                  </div>
                </div>
              </div>
              
              {error && (
                <p className="text-red-500 text-sm text-center">{error}</p>
              )}
              
              <Button
                onClick={() => setStep(2)}
                disabled={!idType || !frontImage || !selfie}
                className="w-full bg-emerald-600 hover:bg-emerald-700"
              >
                Continue
              </Button>
            </>
          )}
          
          {step === 2 && (
            <>
              <div className="text-center">
                <h3 className="text-lg font-semibold mb-2">Review & Submit</h3>
                <p className="text-gray-600 text-sm">Please confirm your information is correct</p>
              </div>
              
              <div className="bg-gray-50 rounded-lg p-4 space-y-3">
                <div className="flex justify-between">
                  <span className="text-gray-600">ID Type:</span>
                  <span className="font-medium">{idTypes.find(t => t.value === idType)?.label}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Front of ID:</span>
                  <span className="text-emerald-600">✓ Uploaded</span>
                </div>
                {backImage && (
                  <div className="flex justify-between">
                    <span className="text-gray-600">Back of ID:</span>
                    <span className="text-emerald-600">✓ Uploaded</span>
                  </div>
                )}
                <div className="flex justify-between">
                  <span className="text-gray-600">Selfie:</span>
                  <span className="text-emerald-600">✓ Uploaded</span>
                </div>
                <div className="flex justify-between border-t pt-3">
                  <span className="text-gray-600">Redemption Amount:</span>
                  <span className="font-bold text-emerald-600">${withdrawAmount}</span>
                </div>
              </div>
              
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                <p className="text-blue-800 text-sm">
                  <strong>Processing Time:</strong> Verification typically takes 3–5 business days. 
                  You will receive an email once your identity is verified.
                </p>
              </div>
              
              <div className="flex gap-3">
                <Button
                  variant="outline"
                  onClick={() => setStep(1)}
                  className="flex-1"
                  disabled={isSubmitting}
                >
                  Back
                </Button>
                <Button
                  onClick={handleSubmit}
                  disabled={isSubmitting}
                  className="flex-1 bg-emerald-600 hover:bg-emerald-700"
                >
                  {isSubmitting ? (
                    <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Submitting...</>
                  ) : (
                    'Submit for Verification'
                  )}
                </Button>
              </div>
            </>
          )}
          
          {step === 3 && (
            <div className="text-center space-y-4">
              <CheckCircle className="h-16 w-16 text-emerald-500 mx-auto" />
              <h3 className="text-xl font-semibold text-emerald-600">Verification Submitted!</h3>
              <p className="text-gray-600">
                Your documents have been received and are being reviewed. 
                We'll notify you by email once verification is complete.
              </p>
              <div className="bg-gray-50 rounded-lg p-4">
                <p className="text-sm text-gray-600">Reference Number:</p>
                <p className="font-mono font-bold text-lg">VER-{Date.now().toString(36).toUpperCase()}</p>
              </div>
              <Button
                onClick={() => {
                  onVerified();
                  onClose();
                }}
                className="w-full bg-emerald-600 hover:bg-emerald-700"
              >
                Done
              </Button>
            </div>
          )}
          
          <p className="text-xs text-gray-500 text-center">
            Your documents are encrypted and stored securely. We never share your 
            personal information with third parties.
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default IDVerificationModal;
